'use client';

import { useEffect, useState } from 'react';
import { Box, Paper, Stack, Typography, Chip, Button, IconButton } from '@mui/material';
import { Bookmark as TemplateIcon, ArrowForward as UseIcon } from '@mui/icons-material';
import useApiCall from '@/services/api/useApiCall';
import { buildApiConfig } from '@/services/api/apiClient';
import { useTranslation } from '@/kit/useTranslation';
import { endPointEnums } from '@/services/api/endpoints';

export default function RecentTemplates({ onApply }) {
  const { t } = useTranslation();
  const { makeApiCall } = useApiCall();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const config = buildApiConfig(endPointEnums.GET_SHIPMENT_TEMPLATES);
        const res = await makeApiCall(config);
        if (res?.success) setTemplates(res.data || []);
      } catch (err) {
        console.error('[RecentTemplates] fetch failed', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [makeApiCall]);

  const handleApply = async (t) => {
    if (!t) return;
    // Apply locally
    onApply(t);
    // Inform server template was used
    try {
      const config = buildApiConfig(endPointEnums.USE_SHIPMENT_TEMPLATE, {
        templateId: t.id,
      });
      await makeApiCall(config);
    } catch (err) {
      console.error('[RecentTemplates] Failed to mark template used', err);
    }
  };

  if (loading)
    return (
      <Typography sx={{ fontSize: '0.85rem' }}>{t('bulkBooking.loadingTemplates')}</Typography>
    );

  if (!templates || templates.length === 0)
    return (
      <Typography sx={{ fontSize: '0.85rem' }}>{t('bulkBooking.noTemplatesFound')}</Typography>
    );

  return (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 1 }}>
        {t('bulkBooking.recentTemplates')}
      </Typography>
      <Stack spacing={1}>
        {templates.slice(0, 6).map((t) => (
          <Paper
            key={t.id}
            elevation={0}
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid #eef2f7',
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                {t.name || `${t.origin} → ${t.destination}`}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {t.origin
                  ? typeof t.origin === 'string'
                    ? t.origin
                    : t.origin.label || t.origin.address
                  : ''}{' '}
                →{' '}
                {t.destination
                  ? typeof t.destination === 'string'
                    ? t.destination
                    : t.destination.label || t.destination.address
                  : ''}
              </Typography>
            </Box>
            <Box>
              <Button
                size="small"
                startIcon={<UseIcon />}
                onClick={() => handleApply(t)}
                sx={{ textTransform: 'none' }}
              >
                {t('bulkBooking.use')}
              </Button>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
